// =====================================================
// PRICING RULES SERVICE
// Stage 2: Participants & Pricing
// CRUD operations for pricing rules
// =====================================================

import { supabase } from '@/supabase.config';
import type { Json } from '@/types/supabase';
import type {
  PricingRule,
  CreatePricingRuleData,
  UpdatePricingRuleData,
} from '@/types/pricing.types';

// ===================
// TYPE DEFINITIONS
// ===================

interface PricingRuleFilters {
  event_id?: string;
  is_active?: boolean;
  search?: string;
}

// ===================
// CRUD OPERATIONS
// ===================

/**
 * Fetch all pricing rules for an event
 */
export async function getPricingRulesByEvent(
  eventId: string,
  filters?: PricingRuleFilters
): Promise<PricingRule[]> {
  let query = supabase
    .from('pricing_rules')
    .select('*')
    .eq('event_id', eventId)
    .order('priority', { ascending: true });

  // Apply filters
  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }

  if (filters?.search) {
    query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching pricing rules:', error);
    throw new Error(`Error al cargar reglas de precios: ${error.message}`);
  }

  // Parse JSONB conditions field
  const rules = (data || []).map((rule) => ({
    ...rule,
    conditions: typeof rule.conditions === 'string' ? JSON.parse(rule.conditions) : rule.conditions,
  })) as PricingRule[];

  return rules;
}

/**
 * Fetch a single pricing rule by ID
 */
export async function getPricingRuleById(id: string): Promise<PricingRule | null> {
  const { data, error } = await supabase.from('pricing_rules').select('*').eq('id', id).single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Not found
    }
    console.error('Error fetching pricing rule:', error);
    throw new Error(`Error al cargar regla de precios: ${error.message}`);
  }

  // Parse JSONB conditions field
  const rule = {
    ...data,
    conditions: typeof data.conditions === 'string' ? JSON.parse(data.conditions) : data.conditions,
  } as PricingRule;

  return rule;
}

/**
 * Create a new pricing rule
 */
export async function createPricingRule(data: CreatePricingRuleData): Promise<PricingRule> {
  const { data: rule, error } = await supabase
    .from('pricing_rules')
    .insert({
      event_id: data.event_id,
      name: data.name,
      description: data.description || null,
      priority: data.priority !== undefined ? data.priority : 0, // Trigger will assign if 0
      is_active: data.is_active !== undefined ? data.is_active : true,
      conditions: data.conditions as unknown as Json, // jsonb column: pass the array, not a JSON string
      action_type: data.action_type,
      action_value: data.action_value,
      created_by: (await supabase.auth.getUser()).data.user?.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating pricing rule:', error);
    throw new Error(`Error al crear regla de precios: ${error.message}`);
  }

  return rule as unknown as PricingRule;
}

/**
 * Update a pricing rule
 */
export async function updatePricingRule(
  id: string,
  data: UpdatePricingRuleData
): Promise<PricingRule> {
  const updateData: Record<string, unknown> = {
    updated_by: (await supabase.auth.getUser()).data.user?.id,
  };

  if (data.name !== undefined) updateData.name = data.name;
  if (data.description !== undefined) updateData.description = data.description;
  if (data.priority !== undefined) updateData.priority = data.priority;
  if (data.is_active !== undefined) updateData.is_active = data.is_active;
  if (data.conditions !== undefined) updateData.conditions = data.conditions as unknown as Json;
  if (data.action_type !== undefined) updateData.action_type = data.action_type;
  if (data.action_value !== undefined) updateData.action_value = data.action_value;

  const { data: rule, error } = await supabase
    .from('pricing_rules')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update(updateData as any)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating pricing rule:', error);
    throw new Error(`Error al actualizar regla de precios: ${error.message}`);
  }

  return rule as unknown as PricingRule;
}

/**
 * Delete a pricing rule
 */
export async function deletePricingRule(id: string): Promise<void> {
  const { error } = await supabase.from('pricing_rules').delete().eq('id', id);

  if (error) {
    console.error('Error deleting pricing rule:', error);
    throw new Error(`Error al eliminar regla de precios: ${error.message}`);
  }
}

/**
 * Toggle rule active status
 */
export async function togglePricingRuleStatus(id: string, isActive: boolean): Promise<PricingRule> {
  return updatePricingRule(id, { is_active: isActive });
}

// ===================
// PRIORITY MANAGEMENT
// ===================

/**
 * Reorder pricing rules by updating priorities
 */
export async function reorderPricingRules(
  _eventId: string,
  ruleIdsInOrder: string[]
): Promise<void> {
  // Update each rule priority individually
  const updatePromises = ruleIdsInOrder.map((ruleId, index) =>
    supabase
      .from('pricing_rules')
      .update({ priority: index + 1 })
      .eq('id', ruleId)
  );

  const results = await Promise.all(updatePromises);
  const error = results.find((r) => r.error)?.error;

  if (error) {
    console.error('Error reordering pricing rules:', error);
    throw new Error(`Error al reordenar reglas de precios: ${error.message}`);
  }
}

/**
 * Swap two rules' priorities atomically (single DB transaction; see
 * swap_pricing_rule_priority in 20261005000003_events_p0_fixes.sql)
 */
async function swapPricingRulePriority(ruleA: string, ruleB: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).rpc('swap_pricing_rule_priority', {
    p_rule_a: ruleA,
    p_rule_b: ruleB,
  });
  if (error) {
    throw new Error(`Error al mover regla: ${error.message}`);
  }
}

/**
 * Move rule up in priority (decrease priority number)
 */
export async function movePricingRuleUp(eventId: string, ruleId: string): Promise<void> {
  // Get all rules for event
  const rules = await getPricingRulesByEvent(eventId);

  // Find current rule and previous rule
  const currentIndex = rules.findIndex((r) => r.id === ruleId);
  if (currentIndex <= 0) {
    return; // Already at top or not found
  }

  await swapPricingRulePriority(rules[currentIndex].id, rules[currentIndex - 1].id);
}

/**
 * Move rule down in priority (increase priority number)
 */
export async function movePricingRuleDown(eventId: string, ruleId: string): Promise<void> {
  // Get all rules for event
  const rules = await getPricingRulesByEvent(eventId);

  // Find current rule and next rule
  const currentIndex = rules.findIndex((r) => r.id === ruleId);
  if (currentIndex < 0 || currentIndex >= rules.length - 1) {
    return; // Already at bottom or not found
  }

  await swapPricingRulePriority(rules[currentIndex].id, rules[currentIndex + 1].id);
}

// ===================
// DUPLICATE & COPY
// ===================

/**
 * Duplicate a pricing rule (useful for creating similar rules)
 */
export async function duplicatePricingRule(id: string): Promise<PricingRule> {
  const original = await getPricingRuleById(id);
  if (!original) {
    throw new Error('Regla de precios no encontrada');
  }

  // Create copy with modified name
  return createPricingRule({
    event_id: original.event_id,
    name: `${original.name} (copia)`,
    description: original.description || undefined,
    priority: undefined, // Will be assigned automatically
    is_active: false, // Copies start inactive
    conditions: original.conditions,
    action_type: original.action_type,
    action_value: original.action_value,
  });
}

/**
 * Copy pricing rules from one event to another
 */
export async function copyPricingRulesToEvent(
  sourceEventId: string,
  targetEventId: string
): Promise<PricingRule[]> {
  const sourceRules = await getPricingRulesByEvent(sourceEventId);

  // Create copies in target event
  const copies = await Promise.all(
    sourceRules.map((rule) =>
      createPricingRule({
        event_id: targetEventId,
        name: rule.name,
        description: rule.description || undefined,
        priority: rule.priority,
        is_active: rule.is_active,
        conditions: rule.conditions,
        action_type: rule.action_type,
        action_value: rule.action_value,
      })
    )
  );

  return copies;
}

// ===================
// STATISTICS
// ===================

/**
 * Get statistics about pricing rule usage
 */
export async function getPricingRuleStatistics(eventId: string) {
  const { data, error } = await supabase
    .from('event_participants')
    .select(
      `
      applied_pricing_rule_id,
      calculated_price,
      pricing_rules:applied_pricing_rule_id (
        id,
        name,
        action_type,
        action_value
      )
    `
    )
    .eq('event_id', eventId)
    .not('applied_pricing_rule_id', 'is', null);

  if (error) {
    console.error('Error fetching pricing statistics:', error);
    throw new Error(`Error al cargar estadísticas de precios: ${error.message}`);
  }

  // Aggregate statistics
  const stats = new Map<
    string,
    {
      rule_id: string;
      rule_name: string;
      count: number;
      total_discount: number;
    }
  >();

  data?.forEach((participant: unknown) => {
    const p = participant as {
      applied_pricing_rule_id: string;
      calculated_price: number;
      pricing_rules: { id: string; name: string; action_type: string; action_value: number };
    };

    const ruleId = p.applied_pricing_rule_id;
    if (!stats.has(ruleId)) {
      stats.set(ruleId, {
        rule_id: ruleId,
        rule_name: p.pricing_rules.name,
        count: 0,
        total_discount: 0,
      });
    }

    const stat = stats.get(ruleId)!;
    stat.count++;
    // TODO: Calculate actual discount (need base price)
  });

  return Array.from(stats.values());
}
